import { useState, useMemo, useEffect, useRef } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import {
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Check,
  Plus,
  Sparkles,
  Loader2,
  AlertCircle,
  HelpCircle,
  Lightbulb,
  MessageSquare,
  Bot,
  Send,
  RotateCcw,
  Download,
  Square,
  CheckSquare,
  Wand2,
  MessagesSquare,
  LayoutGrid,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useConnectionStore } from '@/stores/connectionStore'
import { useAIStore } from '@/stores/aiStore'
import { useDiscoveryStore } from '@/stores/discoveryStore'
import { useSessionStore } from '@/stores/sessionStore'
import { discoveryApi } from '@/lib/api/client'
import { ConversationalCoach } from './ConversationalCoach'
import { getInstancesOfClass } from '@/lib/sparql/queries'
import { getLocalName, NAMESPACES } from '@/lib/ontology/namespaces'
import { CreateInstanceDialog } from '@/components/shared/CreateInstanceDialog'
import { InstanceDetailPanel } from '@/components/shared/InstanceDetailPanel'
import { DiscoveryIntake } from './DiscoveryIntake'
import {
  DISCOVERY_PHASES,
  getNextPhase,
  getPreviousPhase,
  type DiscoveryPhase,
  type DiscoveryEntity,
} from './discoveryPhases'
import { useDiscoveryAssistant, getStaticGuidance, type StreamingState } from '@/hooks/useDiscoveryAssistant'
import { update } from '@/lib/api/sparql-adapter'
import { generateInstanceUri, type ExtractedEntity } from '@/lib/pdf/entityExtractor'

// Phase-level coaching messages
const PHASE_COACHING: Record<string, { greeting: string; focus: string; nextStep: string }> = {
  'customer-discovery': {
    greeting: "Let's start by understanding WHO you're building for.",
    focus: "Focus on defining your target market. Who has the problem you want to solve?",
    nextStep: "Start by adding a Customer Segment - this is the foundation of everything.",
  },
  'problem-validation': {
    greeting: "Now let's dig into the PROBLEMS your customers face.",
    focus: "The goal is to deeply understand pains, jobs, and gains. This is where product-market fit begins.",
    nextStep: "Add a Customer Job first - what task are they trying to accomplish?",
  },
  'value-design': {
    greeting: "Time to design HOW you'll create value.",
    focus: "Connect your solution to the problems you've identified. Make the value proposition crystal clear.",
    nextStep: "Define your Value Proposition - what unique benefit do you offer?",
  },
  'solution-design': {
    greeting: "Let's define WHAT you're building.",
    focus: "Keep it minimal and focused. What's the smallest thing that proves your hypothesis?",
    nextStep: "Start with your core Product, then add key Features.",
  },
  'business-model': {
    greeting: "Finally, let's figure out HOW you'll make money.",
    focus: "A great product needs a sustainable business model. Think revenue and costs.",
    nextStep: "Define your primary Revenue Stream first.",
  },
}

// Discovery mode types
type DiscoveryMode = 'structured' | 'conversational'

export function DiscoveryWorkflow() {
  const { isConnected } = useConnectionStore()
  const { isConfigured: aiConfigured } = useAIStore()
  const { hasCompletedIntake, aiAutoFill, extractedEntities, setExtractedEntities, resetIntake } = useDiscoveryStore()
  const { accountId, projectId, isReady: sessionReady } = useSessionStore()
  const queryClient = useQueryClient()

  // Discovery mode: structured (old 5-phase) vs conversational (new chat-based)
  const [discoveryMode, setDiscoveryMode] = useState<DiscoveryMode>('conversational')
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null)

  const [currentPhaseId, setCurrentPhaseId] = useState(DISCOVERY_PHASES[0].id)
  const [selectedEntity, setSelectedEntity] = useState<DiscoveryEntity | null>(null)
  const [createForEntity, setCreateForEntity] = useState<DiscoveryEntity | null>(null)
  const [selectedInstance, setSelectedInstance] = useState<string | null>(null)
  const [importingEntities, setImportingEntities] = useState(false)
  const [importResult, setImportResult] = useState<{ success: number; failed: number } | null>(null)
  const [selectedEntityIds, setSelectedEntityIds] = useState<Set<string>>(new Set())

  // Fetch existing discovery sessions
  const { data: sessions = [], isLoading: sessionsLoading } = useQuery({
    queryKey: ['discoverySessions', accountId, projectId],
    queryFn: () => {
      if (!accountId || !projectId) return []
      return discoveryApi.listSessions(accountId, projectId)
    },
    enabled: !!accountId && !!projectId,
  })

  // Create new session mutation
  const createSessionMutation = useMutation({
    mutationFn: async (ideaText?: string) => {
      if (!accountId || !projectId) throw new Error('No account/project')
      return discoveryApi.createSession(accountId, projectId, {
        title: ideaText ? `Discovery: ${ideaText.slice(0, 50)}...` : 'New Discovery Session',
        ideaText,
      })
    },
    onSuccess: (session) => {
      setCurrentSessionId(session.id)
      queryClient.invalidateQueries({ queryKey: ['discoverySessions', accountId, projectId] })
    },
  })

  // Auto-select most recent session or create new one when switching to conversational mode
  useEffect(() => {
    if (discoveryMode === 'conversational' && !currentSessionId && sessions.length > 0) {
      // Select the most recent active session
      const activeSession = sessions.find(s => s.status === 'active')
      if (activeSession) {
        setCurrentSessionId(activeSession.id)
      }
    }
  }, [discoveryMode, currentSessionId, sessions])

  // Initialize selected entity IDs when extracted entities change
  useEffect(() => {
    if (extractedEntities.length > 0) {
      setSelectedEntityIds(new Set(extractedEntities.map(e => e.id)))
    }
  }, [extractedEntities])

  const currentPhase = useMemo(
    () => DISCOVERY_PHASES.find((p) => p.id === currentPhaseId) || DISCOVERY_PHASES[0],
    [currentPhaseId]
  )

  const { getGuidance, askQuestion, guidance, chatResponse, streamingState, isLoading: aiLoading, clearGuidance } = useDiscoveryAssistant()

  // Track if we've already auto-selected for this phase
  const autoSelectedPhaseRef = useRef<string | null>(null)

  // Reset selected entity when phase changes
  useEffect(() => {
    setSelectedEntity(null)
    setSelectedInstance(null)
    clearGuidance()
    autoSelectedPhaseRef.current = null
  }, [currentPhaseId, clearGuidance])

  // Auto-select first required entity when phase loads (only once per phase)
  useEffect(() => {
    if (autoSelectedPhaseRef.current !== currentPhase.id) {
      autoSelectedPhaseRef.current = currentPhase.id
      const firstRequired = currentPhase.entities.find(e => e.required)
      if (firstRequired) {
        setSelectedEntity(firstRequired)
      }
    }
  }, [currentPhase])

  // NOTE: We no longer auto-fetch AI guidance - users must explicitly ask via "Ask AI" button

  if (!isConnected) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Card style={{ maxWidth: '400px', textAlign: 'center', padding: '32px' }}>
          <AlertCircle style={{ width: '48px', height: '48px', color: 'var(--color-warning)', margin: '0 auto 16px' }} />
          <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Not Connected</h3>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
            Connect to Fuseki in Settings to start your discovery journey.
          </p>
        </Card>
      </div>
    )
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Header with Mode Toggle and Actions */}
      {hasCompletedIntake && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 16px',
          backgroundColor: 'var(--color-warm-50)',
          borderRadius: '12px',
        }}>
          {/* Mode Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--color-text-muted)' }}>Mode:</span>
            <div style={{
              display: 'flex',
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '4px',
              border: '1px solid var(--color-border)',
            }}>
              <button
                onClick={() => setDiscoveryMode('conversational')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  backgroundColor: discoveryMode === 'conversational' ? 'var(--color-primary)' : 'transparent',
                  color: discoveryMode === 'conversational' ? 'white' : 'var(--color-text-secondary)',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                <MessagesSquare style={{ width: '14px', height: '14px' }} />
                Chat
              </button>
              <button
                onClick={() => setDiscoveryMode('structured')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  backgroundColor: discoveryMode === 'structured' ? 'var(--color-primary)' : 'transparent',
                  color: discoveryMode === 'structured' ? 'white' : 'var(--color-text-secondary)',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                <LayoutGrid style={{ width: '14px', height: '14px' }} />
                Structured
              </button>
            </div>
          </div>

          {/* Session Selector (for conversational mode) */}
          {discoveryMode === 'conversational' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <select
                value={currentSessionId || ''}
                onChange={(e) => {
                  if (e.target.value === 'new') {
                    createSessionMutation.mutate()
                  } else {
                    setCurrentSessionId(e.target.value || null)
                  }
                }}
                style={{
                  padding: '6px 10px',
                  fontSize: '12px',
                  border: '1px solid var(--color-border)',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  minWidth: '180px',
                }}
              >
                <option value="">Select session...</option>
                {sessions.map((session) => (
                  <option key={session.id} value={session.id}>
                    {session.title || `Session ${session.id.slice(0, 8)}`}
                  </option>
                ))}
                <option value="new">+ New Session</option>
              </select>
              {createSessionMutation.isPending && (
                <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} />
              )}
            </div>
          )}

          {/* Restart Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => resetIntake()}
            style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}
          >
            <RotateCcw style={{ width: '14px', height: '14px', marginRight: '6px' }} />
            Restart
          </Button>
        </div>
      )}

      {/* AI Auto-fill Banner - only show when completed with auto-fill and no extracted entities to import */}
      {hasCompletedIntake && aiAutoFill && extractedEntities.length === 0 && (
        <div
          style={{
            padding: '12px 16px',
            borderRadius: '10px',
            backgroundColor: '#5c8a6b10',
            border: '1px solid #5c8a6b30',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <Sparkles style={{ width: '18px', height: '18px', color: '#5c8a6b', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: '13px', color: 'var(--color-text-primary)' }}>
              <strong>AI Suggestions Ready:</strong> Based on your idea, we've pre-filled some suggestions.
              Look for <Sparkles style={{ width: '12px', height: '12px', display: 'inline', verticalAlign: 'middle', color: '#5c8a6b' }} /> icons when creating entities.
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => resetIntake()}
            style={{ fontSize: '12px', flexShrink: 0 }}
          >
            Clear
          </Button>
        </div>
      )}

      {/* Conversational Mode - Full screen chat experience */}
      {hasCompletedIntake && discoveryMode === 'conversational' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <ConversationalCoach
            accountId={accountId || ''}
            projectId={projectId || ''}
            sessionId={currentSessionId || undefined}
            className="flex-1"
          />
        </div>
      )}

      {/* Structured Mode - Phase navigation and entity grid */}
      {(!hasCompletedIntake || discoveryMode === 'structured') && (
        <>
          {/* Phase Navigation - always visible but locked if intake not completed */}
          <PhaseNavigator
            phases={DISCOVERY_PHASES}
            currentPhaseId={currentPhaseId}
            onPhaseChange={hasCompletedIntake ? setCurrentPhaseId : undefined}
            locked={!hasCompletedIntake}
          />

          {/* Main Content - 3 column layout */}
          <div style={{ flex: 1, display: 'flex', gap: '20px', minHeight: 0 }}>
            {/* Left: Intake Panel OR AI Coach Panel */}
            {!hasCompletedIntake ? (
              <div style={{ width: '360px', flexShrink: 0 }}>
                <DiscoveryIntake onComplete={() => {}} />
              </div>
            ) : (
              <AICoachPanel
                phase={currentPhase}
                entity={selectedEntity}
                guidance={guidance}
                chatResponse={chatResponse}
                streamingState={streamingState}
                isLoading={aiLoading}
                aiConfigured={aiConfigured}
                onAskAI={() => {
                  if (selectedEntity) {
                    getGuidance({
                      phase: currentPhase,
                      entity: selectedEntity,
                      existingEntities: [],
                    })
                  }
                }}
                onAskQuestion={(question) => {
                  if (selectedEntity) {
                    askQuestion(question, {
                      phase: currentPhase,
                      entity: selectedEntity,
                      existingEntities: [],
                    })
                  }
                }}
              />
            )}

        {/* Center: Import Panel OR Phase Content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'auto' }}>
          {hasCompletedIntake && extractedEntities.length > 0 ? (
            <ExtractedEntitiesImportPanel
              entities={extractedEntities}
              selectedIds={selectedEntityIds}
              onToggleSelection={(id) => {
                setSelectedEntityIds(prev => {
                  const next = new Set(prev)
                  if (next.has(id)) {
                    next.delete(id)
                  } else {
                    next.add(id)
                  }
                  return next
                })
              }}
              onSelectAll={() => setSelectedEntityIds(new Set(extractedEntities.map(e => e.id)))}
              onDeselectAll={() => setSelectedEntityIds(new Set())}
              isImporting={importingEntities}
              importResult={importResult}
              onImport={async () => {
                if (!isConnected) return
                const entitiesToImport = extractedEntities.filter(e => selectedEntityIds.has(e.id))
                if (entitiesToImport.length === 0) return

                setImportingEntities(true)
                setImportResult(null)

                let success = 0
                let failed = 0

                for (const entity of entitiesToImport) {
                  try {
                    const instanceUri = generateInstanceUri(entity.suggestedClass.localName, entity.text)
                    // Add source:ai marker in the comment to track AI-extracted entities
                    const sparql = `
                      PREFIX rdf: <${NAMESPACES.rdf}>
                      PREFIX rdfs: <${NAMESPACES.rdfs}>
                      INSERT DATA {
                        <${instanceUri}> rdf:type <${entity.suggestedClass.uri}> .
                        <${instanceUri}> rdfs:label "${entity.text.replace(/"/g, '\\"')}" .
                        <${instanceUri}> rdfs:comment "[AI-Extracted] ${entity.context.replace(/"/g, '\\"').slice(0, 480)}" .
                      }
                    `
                    await update(sparql)
                    success++
                  } catch {
                    failed++
                  }
                }

                setImportResult({ success, failed })
                setImportingEntities(false)

                // Invalidate queries to refresh entity lists
                queryClient.invalidateQueries({ queryKey: ['instances'] })
                queryClient.invalidateQueries({ queryKey: ['discoveryInstances'] })
                queryClient.invalidateQueries({ queryKey: ['instanceCount'] })

                // Clear extracted entities after successful import
                if (failed === 0) {
                  setTimeout(() => {
                    setExtractedEntities([])
                    setSelectedEntityIds(new Set())
                  }, 2000)
                }
              }}
              onDismiss={() => {
                setExtractedEntities([])
                setSelectedEntityIds(new Set())
              }}
            />
          ) : (
            <PhaseContent
              phase={currentPhase}
              selectedEntity={selectedEntity}
              onSelectEntity={setSelectedEntity}
              onCreateEntity={setCreateForEntity}
              onSelectInstance={setSelectedInstance}
              selectedInstance={selectedInstance}
            />
          )}
        </div>

        {/* Right: Instance Detail Panel (when selected) */}
        {selectedInstance && (
          <InstanceDetailPanel
            instanceUri={selectedInstance}
            color={currentPhase.color}
            bgColor={currentPhase.bgColor}
            onClose={() => setSelectedInstance(null)}
            onNavigate={(uri) => setSelectedInstance(uri)}
            onDeleted={() => {
              queryClient.invalidateQueries({ queryKey: ['discoveryInstances'] })
              setSelectedInstance(null)
            }}
          />
          )}
        </div>

          {/* Phase Navigation Footer */}
          <PhaseNavigationFooter
            currentPhase={currentPhase}
            onPrevious={() => {
              const prev = getPreviousPhase(currentPhaseId)
              if (prev) setCurrentPhaseId(prev.id)
            }}
            onNext={() => {
              const next = getNextPhase(currentPhaseId)
              if (next) setCurrentPhaseId(next.id)
            }}
          />
        </>
      )}

      {/* Create Instance Dialog - works in both modes */}
      {createForEntity && (
        <CreateInstanceDialog
          classUri={createForEntity.classUri}
          classLabel={createForEntity.label}
          color={currentPhase.color}
          bgColor={currentPhase.bgColor}
          onClose={() => setCreateForEntity(null)}
          onCreated={(uri) => {
            queryClient.invalidateQueries({ queryKey: ['discoveryInstances'] })
            setSelectedInstance(uri)
          }}
        />
      )}
    </div>
  )
}

// ============================================
// Extracted Entities Import Panel - Full Page View
// ============================================
interface ExtractedEntitiesImportPanelProps {
  entities: ExtractedEntity[]
  selectedIds: Set<string>
  onToggleSelection: (id: string) => void
  onSelectAll: () => void
  onDeselectAll: () => void
  isImporting: boolean
  importResult: { success: number; failed: number } | null
  onImport: () => void
  onDismiss: () => void
}

const DOMAIN_CONFIG: Record<string, { color: string; bg: string; label: string; description: string }> = {
  Market: {
    color: '#5c8a6b',
    bg: '#5c8a6b08',
    label: 'Market Layer',
    description: 'WHO you serve - customers, segments, competitors, industry context',
  },
  Business: {
    color: '#c9a227',
    bg: '#c9a22708',
    label: 'Business Layer',
    description: 'HOW you create value - channels, revenue, partnerships, resources',
  },
  Solution: {
    color: '#4a90a4',
    bg: '#4a90a408',
    label: 'Solution Layer',
    description: 'WHAT you build - products, features, requirements, user stories',
  },
}

function ExtractedEntitiesImportPanel({
  entities,
  selectedIds,
  onToggleSelection,
  onSelectAll,
  onDeselectAll,
  isImporting,
  importResult,
  onImport,
  onDismiss,
}: ExtractedEntitiesImportPanelProps) {
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set(['Market', 'Business', 'Solution']))

  // Group entities by domain, then by class
  const groupedByDomain = useMemo(() => {
    const result: Record<string, Record<string, ExtractedEntity[]>> = {}
    for (const entity of entities) {
      const domain = entity.domain
      const className = entity.suggestedClass.label
      if (!result[domain]) result[domain] = {}
      if (!result[domain][className]) result[domain][className] = []
      result[domain][className].push(entity)
    }
    return result
  }, [entities])

  const toggleDomain = (domain: string) => {
    setExpandedDomains(prev => {
      const next = new Set(prev)
      if (next.has(domain)) {
        next.delete(domain)
      } else {
        next.add(domain)
      }
      return next
    })
  }

  const selectedCount = selectedIds.size
  const allSelected = selectedCount === entities.length
  const noneSelected = selectedCount === 0

  // Count selected per domain
  const selectedPerDomain = useMemo(() => {
    const counts: Record<string, { selected: number; total: number }> = {}
    for (const entity of entities) {
      const domain = entity.domain
      if (!counts[domain]) counts[domain] = { selected: 0, total: 0 }
      counts[domain].total++
      if (selectedIds.has(entity.id)) counts[domain].selected++
    }
    return counts
  }, [entities, selectedIds])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Header Card */}
      <Card
        style={{
          background: 'linear-gradient(135deg, #5c8a6b15 0%, #c9a22715 50%, #4a90a415 100%)',
          border: '2px solid var(--color-border)',
        }}
      >
        <CardContent style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #5c8a6b 0%, #4a90a4 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(92, 138, 107, 0.3)',
              }}
            >
              <Wand2 style={{ width: '28px', height: '28px', color: 'white' }} />
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--color-text)', marginBottom: '6px' }}>
                AI-Extracted Entities
              </h2>
              <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', lineHeight: 1.5, marginBottom: '16px' }}>
                We analyzed your document and extracted <strong>{entities.length} entities</strong> across 3 layers.
                Review and select which entities to import into your knowledge graph.
              </p>

              {/* Import Result Banner */}
              {importResult && (
                <div
                  style={{
                    padding: '12px 16px',
                    borderRadius: '10px',
                    backgroundColor: importResult.failed === 0 ? '#5c8a6b15' : '#f5a62320',
                    border: `1px solid ${importResult.failed === 0 ? '#5c8a6b40' : '#f5a62340'}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  {importResult.failed === 0 ? (
                    <>
                      <Check style={{ width: '20px', height: '20px', color: '#5c8a6b' }} />
                      <div>
                        <p style={{ fontSize: '14px', fontWeight: 600, color: '#5c8a6b' }}>
                          Successfully imported {importResult.success} entities!
                        </p>
                        <p style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
                          They are now available in your discovery workflow with an AI badge.
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <AlertCircle style={{ width: '20px', height: '20px', color: '#f5a623' }} />
                      <div>
                        <p style={{ fontSize: '14px', fontWeight: 600, color: '#f5a623' }}>
                          Imported {importResult.success} of {importResult.success + importResult.failed} entities
                        </p>
                        <p style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
                          {importResult.failed} entities failed to import.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Selection Controls & Action Buttons */}
              {!importResult && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={allSelected ? onDeselectAll : onSelectAll}
                      style={{ fontSize: '12px' }}
                    >
                      {allSelected ? (
                        <>
                          <Square style={{ width: '14px', height: '14px', marginRight: '6px' }} />
                          Deselect All
                        </>
                      ) : (
                        <>
                          <CheckSquare style={{ width: '14px', height: '14px', marginRight: '6px' }} />
                          Select All
                        </>
                      )}
                    </Button>
                    <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
                      {selectedCount} of {entities.length} selected
                    </span>
                  </div>

                  <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
                    <Button
                      variant="outline"
                      onClick={onDismiss}
                      disabled={isImporting}
                    >
                      Skip Import
                    </Button>
                    <Button
                      onClick={onImport}
                      disabled={isImporting || noneSelected}
                      style={{
                        backgroundColor: '#5c8a6b',
                        color: 'white',
                        opacity: noneSelected ? 0.5 : 1,
                      }}
                    >
                      {isImporting ? (
                        <>
                          <Loader2 style={{ width: '16px', height: '16px', marginRight: '8px', animation: 'spin 1s linear infinite' }} />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Download style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                          Import {selectedCount} Entities
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Domain Sections */}
      {(['Market', 'Business', 'Solution'] as const).map((domain) => {
        const config = DOMAIN_CONFIG[domain]
        const domainGroups = groupedByDomain[domain] || {}
        const domainCounts = selectedPerDomain[domain] || { selected: 0, total: 0 }
        const isExpanded = expandedDomains.has(domain)
        const entityCount = Object.values(domainGroups).flat().length

        if (entityCount === 0) return null

        return (
          <Card
            key={domain}
            style={{
              border: `2px solid ${config.color}30`,
              backgroundColor: config.bg,
              overflow: 'hidden',
            }}
          >
            {/* Domain Header - Clickable to expand/collapse */}
            <button
              onClick={() => toggleDomain(domain)}
              style={{
                width: '100%',
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                background: 'none',
                border: 'none',
                borderBottom: isExpanded ? `1px solid ${config.color}20` : 'none',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  backgroundColor: config.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ChevronDown
                  style={{
                    width: '20px',
                    height: '20px',
                    color: 'white',
                    transition: 'transform 0.2s',
                    transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, color: config.color }}>
                    {config.label}
                  </h3>
                  <Badge style={{ backgroundColor: `${config.color}20`, color: config.color, fontSize: '11px' }}>
                    {domainCounts.selected}/{domainCounts.total} selected
                  </Badge>
                </div>
                <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '2px' }}>
                  {config.description}
                </p>
              </div>
            </button>

            {/* Domain Content - Entity Groups */}
            {isExpanded && (
              <CardContent style={{ padding: '16px 20px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {Object.entries(domainGroups).map(([className, classEntities]) => (
                    <div key={className}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: config.color }}>
                          {className}
                        </span>
                        <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
                          ({classEntities.length})
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {classEntities.map((entity) => {
                          const isSelected = selectedIds.has(entity.id)
                          return (
                            <button
                              key={entity.id}
                              onClick={() => onToggleSelection(entity.id)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 12px',
                                backgroundColor: isSelected ? 'white' : 'transparent',
                                border: isSelected ? `2px solid ${config.color}` : '2px solid transparent',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'all 0.15s',
                                maxWidth: '300px',
                                textAlign: 'left',
                                boxShadow: isSelected ? `0 2px 8px ${config.color}20` : 'none',
                              }}
                              title={entity.text}
                            >
                              {isSelected ? (
                                <CheckSquare style={{ width: '16px', height: '16px', color: config.color, flexShrink: 0 }} />
                              ) : (
                                <Square style={{ width: '16px', height: '16px', color: 'var(--color-text-muted)', flexShrink: 0 }} />
                              )}
                              <span
                                style={{
                                  fontSize: '12px',
                                  color: isSelected ? 'var(--color-text)' : 'var(--color-text-secondary)',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {entity.text}
                              </span>
                              <Sparkles
                                style={{
                                  width: '12px',
                                  height: '12px',
                                  color: config.color,
                                  opacity: 0.6,
                                  flexShrink: 0,
                                }}
                              />
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )
      })}
    </div>
  )
}

// ============================================
// AI Coach Panel - Always Visible
// ============================================
interface AICoachPanelProps {
  phase: DiscoveryPhase
  entity: DiscoveryEntity | null
  guidance: { questions: string[]; suggestions: string[]; tips: string[]; example?: string } | null
  chatResponse: string | null
  streamingState: StreamingState
  isLoading: boolean
  aiConfigured: boolean
  onAskAI: () => void
  onAskQuestion: (question: string) => void
}

function AICoachPanel({ phase, entity, guidance, chatResponse, streamingState, isLoading, aiConfigured, onAskAI, onAskQuestion }: AICoachPanelProps) {
  const [chatInput, setChatInput] = useState('')
  const coaching = PHASE_COACHING[phase.id]
  // Always show static guidance first, only show AI guidance if explicitly requested
  const staticGuidance = entity ? getStaticGuidance(phase, entity) : null
  const displayGuidance = guidance || staticGuidance
  const isStreaming = streamingState.isStreaming && streamingState.rawText.length > 0
  const hasAIGuidance = guidance !== null

  return (
    <Card
      style={{
        width: '320px',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'linear-gradient(180deg, var(--color-warm-50) 0%, var(--color-surface) 100%)',
        border: `2px solid ${phase.color}30`,
      }}
    >
      {/* Coach Header */}
      <CardHeader
        style={{
          padding: '16px',
          borderBottom: '1px solid var(--color-border)',
          backgroundColor: phase.bgColor,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${phase.color} 0%, ${phase.color}dd 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 12px ${phase.color}40`,
            }}
          >
            {aiConfigured ? (
              <Sparkles style={{ width: '22px', height: '22px', color: 'white' }} />
            ) : (
              <Bot style={{ width: '22px', height: '22px', color: 'white' }} />
            )}
          </div>
          <div>
            <CardTitle style={{ fontSize: '16px', color: phase.color, fontWeight: 700 }}>
              {aiConfigured ? 'AI Coach' : 'Discovery Guide'}
            </CardTitle>
            <p style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
              Phase {phase.number}: {phase.title}
            </p>
          </div>
        </div>
      </CardHeader>

      <CardContent style={{ flex: 1, overflow: 'auto', padding: '0' }}>
        {/* Phase Greeting */}
        <div
          style={{
            padding: '16px',
            backgroundColor: phase.bgColor,
            borderBottom: `1px solid ${phase.color}20`,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
            }}
          >
            <MessageSquare style={{ width: '16px', height: '16px', color: phase.color, flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{ fontSize: '14px', fontWeight: 600, color: phase.color, marginBottom: '6px' }}>
                {coaching?.greeting}
              </p>
              <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                {coaching?.focus}
              </p>
            </div>
          </div>
        </div>

        {/* Loading/Streaming State */}
        {isLoading && !isStreaming && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px', gap: '12px' }}>
            <Loader2 style={{ width: '28px', height: '28px', color: phase.color, animation: 'spin 1s linear infinite' }} />
            <p style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>Thinking...</p>
          </div>
        )}

        {/* Streaming Response */}
        {isStreaming && entity && (
          <div style={{ padding: '16px' }}>
            {/* Current Focus */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '16px',
                padding: '10px 12px',
                backgroundColor: 'white',
                borderRadius: '8px',
                border: `1px solid ${phase.color}30`,
              }}
            >
              {(() => {
                const Icon = entity.icon
                return <Icon style={{ width: '16px', height: '16px', color: phase.color }} />
              })()}
              <span style={{ fontSize: '13px', fontWeight: 600, color: phase.color }}>
                Defining: {entity.label}
              </span>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: phase.color,
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                />
                <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>Streaming...</span>
              </div>
            </div>

            {/* Streaming Text Display */}
            <div
              style={{
                padding: '16px',
                backgroundColor: 'white',
                borderRadius: '10px',
                border: '1px solid var(--color-border)',
                fontSize: '13px',
                lineHeight: 1.7,
                color: 'var(--color-text-secondary)',
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                maxHeight: '400px',
                overflow: 'auto',
              }}
            >
              {streamingState.rawText}
              <span
                style={{
                  display: 'inline-block',
                  width: '2px',
                  height: '14px',
                  backgroundColor: phase.color,
                  marginLeft: '2px',
                  animation: 'blink 1s step-end infinite',
                }}
              />
            </div>
          </div>
        )}

        {/* Entity-specific guidance */}
        {!isLoading && !isStreaming && entity && displayGuidance && (
          <div style={{ padding: '16px' }}>
            {/* Current Focus */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '16px',
                padding: '10px 12px',
                backgroundColor: 'white',
                borderRadius: '8px',
                border: `1px solid ${phase.color}30`,
              }}
            >
              {(() => {
                const Icon = entity.icon
                return <Icon style={{ width: '16px', height: '16px', color: phase.color }} />
              })()}
              <span style={{ fontSize: '13px', fontWeight: 600, color: phase.color }}>
                Defining: {entity.label}
              </span>
              {hasAIGuidance && (
                <Badge style={{ marginLeft: 'auto', backgroundColor: `${phase.color}20`, color: phase.color, fontSize: '9px' }}>
                  <Sparkles style={{ width: '10px', height: '10px', marginRight: '4px' }} />
                  AI
                </Badge>
              )}
            </div>

            {/* Collaborative: Framework */}
            {displayGuidance.framework && (
              <div
                style={{
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: `${phase.color}08`,
                  borderRadius: '10px',
                  border: `1px solid ${phase.color}20`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                  <Sparkles style={{ width: '14px', height: '14px', color: phase.color }} />
                  <h4 style={{ fontSize: '11px', fontWeight: 700, color: phase.color, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Framework
                  </h4>
                </div>
                <p style={{ fontSize: '12px', color: 'var(--color-text)', lineHeight: 1.6 }}>
                  {displayGuidance.framework}
                </p>
              </div>
            )}

            {/* Collaborative: Acknowledge */}
            {displayGuidance.acknowledge && (
              <div
                style={{
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: '#5c8a6b10',
                  borderRadius: '10px',
                  borderLeft: '3px solid #5c8a6b',
                }}
              >
                <p style={{ fontSize: '12px', color: '#5c8a6b', lineHeight: 1.6, fontStyle: 'italic' }}>
                  {displayGuidance.acknowledge}
                </p>
              </div>
            )}

            {/* Questions */}
            {displayGuidance.questions.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                  <HelpCircle style={{ width: '14px', height: '14px', color: phase.color }} />
                  <h4 style={{ fontSize: '11px', fontWeight: 700, color: phase.color, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {displayGuidance.framework ? 'Guiding Questions' : 'Ask Yourself'}
                  </h4>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {displayGuidance.questions.map((q, i) => (
                    <div
                      key={i}
                      style={{
                        padding: '10px 12px',
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        border: '1px solid var(--color-border)',
                        fontSize: '12px',
                        color: 'var(--color-text)',
                        lineHeight: 1.5,
                      }}
                    >
                      {q}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tips */}
            {displayGuidance.suggestions.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                  <Lightbulb style={{ width: '14px', height: '14px', color: 'var(--color-warning)' }} />
                  <h4 style={{ fontSize: '11px', fontWeight: 700, color: 'var(--color-warning)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Pro Tips
                  </h4>
                </div>
                <ul style={{ margin: 0, paddingLeft: '16px', listStyleType: 'disc' }}>
                  {displayGuidance.suggestions.map((s, i) => (
                    <li key={i} style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginBottom: '6px', lineHeight: 1.5 }}>
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Example */}
            {displayGuidance.example && (
              <div
                style={{
                  padding: '12px',
                  backgroundColor: `${phase.color}10`,
                  borderRadius: '8px',
                  borderLeft: `3px solid ${phase.color}`,
                }}
              >
                <p style={{ fontSize: '10px', fontWeight: 700, color: phase.color, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Example
                </p>
                <p style={{ fontSize: '12px', color: 'var(--color-text)', lineHeight: 1.5, fontStyle: 'italic' }}>
                  "{displayGuidance.example}"
                </p>
              </div>
            )}

            {/* Collaborative: Opportunity to go deeper */}
            {displayGuidance.opportunity && (
              <div
                style={{
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: '#c9a22710',
                  borderRadius: '10px',
                  border: '1px dashed #c9a22740',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                  <Lightbulb style={{ width: '14px', height: '14px', color: '#c9a227' }} />
                  <span style={{ fontSize: '10px', fontWeight: 700, color: '#c9a227', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Go Deeper
                  </span>
                </div>
                <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                  {displayGuidance.opportunity}
                </p>
              </div>
            )}

            {/* Ask AI Button - only show when not already showing AI guidance */}
            {aiConfigured && !hasAIGuidance && (
              <Button
                variant="outline"
                size="sm"
                onClick={onAskAI}
                disabled={isLoading}
                style={{ width: '100%', marginTop: '16px', borderColor: `${phase.color}40`, color: phase.color }}
              >
                <Sparkles style={{ width: '14px', height: '14px', marginRight: '6px' }} />
                Get AI Suggestions
              </Button>
            )}

            {/* Chat Response Display */}
            {chatResponse && (
              <div
                style={{
                  marginTop: '16px',
                  padding: '12px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: `1px solid ${phase.color}30`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                  <Bot style={{ width: '14px', height: '14px', color: phase.color }} />
                  <h4 style={{ fontSize: '11px', fontWeight: 700, color: phase.color, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    AI Response
                  </h4>
                </div>
                <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {chatResponse}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Chat Input - always show when entity selected and AI configured */}
        {entity && aiConfigured && !isLoading && !isStreaming && (
          <div style={{ padding: '0 16px 16px', marginTop: 'auto' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && chatInput.trim()) {
                    onAskQuestion(chatInput)
                    setChatInput('')
                  }
                }}
                placeholder="Ask a question about this entity..."
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  fontSize: '12px',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  outline: 'none',
                }}
              />
              <Button
                size="sm"
                onClick={() => {
                  if (chatInput.trim()) {
                    onAskQuestion(chatInput)
                    setChatInput('')
                  }
                }}
                disabled={!chatInput.trim()}
                style={{ backgroundColor: phase.color, color: 'white' }}
              >
                <Send style={{ width: '14px', height: '14px' }} />
              </Button>
            </div>
          </div>
        )}

        {/* No entity selected - show next step */}
        {!isLoading && !entity && (
          <div style={{ padding: '16px' }}>
            <div
              style={{
                padding: '16px',
                backgroundColor: 'white',
                borderRadius: '10px',
                border: `1px dashed ${phase.color}40`,
                textAlign: 'center',
              }}
            >
              <Send style={{ width: '24px', height: '24px', color: phase.color, margin: '0 auto 12px' }} />
              <p style={{ fontSize: '13px', color: 'var(--color-text)', marginBottom: '8px' }}>
                <strong>Next Step:</strong>
              </p>
              <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
                {coaching?.nextStep}
              </p>
              <p style={{ fontSize: '11px', color: 'var(--color-text-muted)', marginTop: '12px' }}>
                Click on an entity card to get coaching
              </p>
            </div>
          </div>
        )}

        {/* AI not configured notice */}
        {!aiConfigured && (
          <div
            style={{
              margin: '16px',
              padding: '12px',
              backgroundColor: `${phase.color}08`,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              border: `1px solid ${phase.color}20`,
            }}
          >
            <Sparkles style={{ width: '16px', height: '16px', color: phase.color, flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{ fontSize: '11px', fontWeight: 600, color: phase.color, marginBottom: '4px' }}>
                Enable AI for smarter guidance
              </p>
              <p style={{ fontSize: '10px', color: 'var(--color-text-muted)', lineHeight: 1.4 }}>
                Add your Anthropic API key in Settings
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ============================================
// Phase Navigator (top bar with all phases)
// ============================================
interface PhaseNavigatorProps {
  phases: DiscoveryPhase[]
  currentPhaseId: string
  onPhaseChange?: (id: string) => void
  locked?: boolean
}

function PhaseNavigator({ phases, currentPhaseId, onPhaseChange, locked = false }: PhaseNavigatorProps) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '8px 12px',
        backgroundColor: 'var(--color-warm-50)',
        borderRadius: '12px',
        overflow: 'auto',
        opacity: locked ? 0.6 : 1,
        position: 'relative',
      }}
    >
      {locked && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            borderRadius: '12px',
            zIndex: 5,
          }}
        >
          <span style={{ fontSize: '13px', color: 'var(--color-text-muted)', fontWeight: 500 }}>
            Complete the intake to unlock phases →
          </span>
        </div>
      )}
      {phases.map((phase, index) => {
        const isCurrent = phase.id === currentPhaseId
        const isPast = phases.findIndex((p) => p.id === currentPhaseId) > index

        return (
          <div key={phase.id} style={{ display: 'flex', alignItems: 'center' }}>
            <button
              onClick={() => !locked && onPhaseChange?.(phase.id)}
              disabled={locked}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                backgroundColor: isCurrent ? phase.bgColor : 'transparent',
                border: isCurrent ? `2px solid ${phase.color}` : '2px solid transparent',
                borderRadius: '10px',
                cursor: locked ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: isCurrent || isPast ? 1 : 0.5,
              }}
            >
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: isPast ? phase.color : isCurrent ? phase.color : 'var(--color-warm-200)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: isPast || isCurrent ? 'white' : 'var(--color-text-muted)',
                  fontSize: '12px',
                  fontWeight: 600,
                }}
              >
                {isPast ? <Check style={{ width: '14px', height: '14px' }} /> : phase.number}
              </div>
              <span
                style={{
                  fontSize: '13px',
                  fontWeight: isCurrent ? 600 : 500,
                  color: isCurrent ? phase.color : 'var(--color-text-secondary)',
                  whiteSpace: 'nowrap',
                }}
              >
                {phase.title}
              </span>
            </button>

            {index < phases.length - 1 && (
              <ChevronRight
                style={{
                  width: '16px',
                  height: '16px',
                  color: 'var(--color-text-muted)',
                  margin: '0 4px',
                  flexShrink: 0,
                }}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ============================================
// Phase Content (entities grid)
// ============================================
interface PhaseContentProps {
  phase: DiscoveryPhase
  selectedEntity: DiscoveryEntity | null
  onSelectEntity: (entity: DiscoveryEntity | null) => void
  onCreateEntity: (entity: DiscoveryEntity) => void
  onSelectInstance: (uri: string | null) => void
  selectedInstance: string | null
}

function PhaseContent({
  phase,
  selectedEntity,
  onSelectEntity,
  onCreateEntity,
  onSelectInstance,
  selectedInstance,
}: PhaseContentProps) {
  const Icon = phase.icon

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Phase Header */}
      <div
        style={{
          padding: '24px',
          backgroundColor: phase.bgColor,
          borderRadius: '16px',
          border: `2px solid ${phase.color}20`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
          <div
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              backgroundColor: phase.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon style={{ width: '24px', height: '24px', color: 'white' }} />
          </div>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: phase.color, marginBottom: '4px' }}>
              Phase {phase.number}: {phase.title}
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--color-text-secondary)', marginBottom: '8px' }}>
              {phase.subtitle}
            </p>
            <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
              {phase.description}
            </p>
          </div>
        </div>
      </div>

      {/* Entity Cards Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '16px',
        }}
      >
        {phase.entities.map((entity) => (
          <EntityCard
            key={entity.classUri}
            entity={entity}
            phase={phase}
            isSelected={selectedEntity?.classUri === entity.classUri}
            onSelect={() => onSelectEntity(selectedEntity?.classUri === entity.classUri ? null : entity)}
            onCreate={() => onCreateEntity(entity)}
            onSelectInstance={onSelectInstance}
            selectedInstance={selectedInstance}
          />
        ))}
      </div>
    </div>
  )
}

// ============================================
// Entity Card
// ============================================
interface EntityCardProps {
  entity: DiscoveryEntity
  phase: DiscoveryPhase
  isSelected: boolean
  onSelect: () => void
  onCreate: () => void
  onSelectInstance: (uri: string | null) => void
  selectedInstance: string | null
}

function EntityCard({
  entity,
  phase,
  isSelected,
  onSelect,
  onCreate,
  onSelectInstance,
  selectedInstance,
}: EntityCardProps) {
  const Icon = entity.icon

  // Fetch instances of this entity type
  const { data: instances = [], isLoading } = useQuery({
    queryKey: ['discoveryInstances', entity.classUri],
    queryFn: async () => {
      try {
        const result = await getInstancesOfClass(entity.classUri, 50, 0)
        return result.results.bindings.map((b) => ({
          uri: b.instance?.value || '',
          label: b.label?.value || getLocalName(b.instance?.value || ''),
          isAiExtracted: b.comment?.value?.startsWith('[AI-Extracted]') || false,
        }))
      } catch {
        return []
      }
    },
    staleTime: 30000,
  })

  const isComplete = entity.required && entity.minCount ? instances.length >= entity.minCount : instances.length > 0

  return (
    <Card
      style={{
        border: isSelected ? `2px solid ${phase.color}` : '2px solid var(--color-border)',
        backgroundColor: isSelected ? phase.bgColor : 'var(--color-surface)',
        transition: 'all 0.2s',
        cursor: 'pointer',
        boxShadow: isSelected ? `0 4px 12px ${phase.color}20` : 'none',
      }}
      onClick={onSelect}
    >
      <CardHeader style={{ padding: '16px', paddingBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              backgroundColor: phase.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon style={{ width: '18px', height: '18px', color: 'white' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CardTitle style={{ fontSize: '14px', color: phase.color }}>
                {entity.label}
              </CardTitle>
              {entity.required && (
                <Badge style={{ fontSize: '9px', backgroundColor: 'var(--color-error)', color: 'white' }}>
                  Required
                </Badge>
              )}
            </div>
            <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '2px' }}>
              {entity.description}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isComplete && (
              <div
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-business)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Check style={{ width: '14px', height: '14px', color: 'white' }} />
              </div>
            )}
            <Badge style={{ backgroundColor: phase.color, color: 'white', fontSize: '11px' }}>
              {isLoading ? '...' : instances.length}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent style={{ padding: '0 16px 16px' }}>
        {/* Instance List */}
        {isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
            <Loader2 style={{ width: '20px', height: '20px', color: phase.color, animation: 'spin 1s linear infinite' }} />
          </div>
        ) : instances.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '16px' }}>
            <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: '12px' }}>
              No {entity.label.toLowerCase()}s defined yet
            </p>
            <Button
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                onCreate()
              }}
              style={{ backgroundColor: phase.color, color: 'white' }}
            >
              <Plus style={{ width: '14px', height: '14px', marginRight: '6px' }} />
              Add {entity.label}
            </Button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
              {instances.slice(0, 6).map((instance) => (
                <div
                  key={instance.uri}
                  onClick={(e) => {
                    e.stopPropagation()
                    onSelectInstance(selectedInstance === instance.uri ? null : instance.uri)
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '6px 10px',
                    backgroundColor: selectedInstance === instance.uri ? phase.color : 'white',
                    color: selectedInstance === instance.uri ? 'white' : 'var(--color-text)',
                    borderRadius: '6px',
                    fontSize: '11px',
                    cursor: 'pointer',
                    border: `1px solid ${phase.color}40`,
                    transition: 'all 0.15s',
                  }}
                  title={instance.isAiExtracted ? 'AI-extracted from document' : undefined}
                >
                  {instance.isAiExtracted && (
                    <Sparkles
                      style={{
                        width: '10px',
                        height: '10px',
                        color: selectedInstance === instance.uri ? 'white' : phase.color,
                        opacity: 0.8,
                      }}
                    />
                  )}
                  {instance.label}
                </div>
              ))}
              {instances.length > 6 && (
                <Badge variant="outline" style={{ fontSize: '10px' }}>
                  +{instances.length - 6} more
                </Badge>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                onCreate()
              }}
              style={{ width: '100%', borderColor: `${phase.color}40`, color: phase.color }}
            >
              <Plus style={{ width: '14px', height: '14px', marginRight: '6px' }} />
              Add Another
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  )
}

// ============================================
// Phase Navigation Footer
// ============================================
interface PhaseNavigationFooterProps {
  currentPhase: DiscoveryPhase
  onPrevious: () => void
  onNext: () => void
}

function PhaseNavigationFooter({ currentPhase, onPrevious, onNext }: PhaseNavigationFooterProps) {
  const prevPhase = getPreviousPhase(currentPhase.id)
  const nextPhase = getNextPhase(currentPhase.id)

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        backgroundColor: 'var(--color-warm-50)',
        borderRadius: '12px',
      }}
    >
      {/* Previous */}
      {prevPhase ? (
        <Button variant="outline" onClick={onPrevious}>
          <ChevronLeft style={{ width: '16px', height: '16px', marginRight: '6px' }} />
          {prevPhase.title}
        </Button>
      ) : (
        <div />
      )}

      {/* Current Phase Indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
          Phase {currentPhase.number} of {DISCOVERY_PHASES.length}
        </span>
      </div>

      {/* Next */}
      {nextPhase ? (
        <Button onClick={onNext} style={{ backgroundColor: currentPhase.color, color: 'white' }}>
          {nextPhase.title}
          <ChevronRight style={{ width: '16px', height: '16px', marginLeft: '6px' }} />
        </Button>
      ) : (
        <div
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--color-business)',
            color: 'white',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Check style={{ width: '16px', height: '16px' }} />
          Discovery Complete
        </div>
      )}
    </div>
  )
}
